import { useUser } from '@myosh/myosh-login';
import {
  ContinuousScroller,
  ContinuousScrollerDataLoadingIndicator,
  type JsonData,
  type JsonDataItem,
  type JsonDataWrapper,
  Loader,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
  useTestTag,
} from '@myosh/odin-components';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { AssignedRecordItem } from '../@types/records';
import { useGetMyActivitiesQuery } from '../redux/services/record';
import { getTextColor } from '../utils/dom-utils';

interface CustomActionScrollProperties {
  showHeader?: boolean;
}
const CustomActionScroll = ({ showHeader = true }: CustomActionScrollProperties) => {
  const [scrollElement, setScrollElement] = useState<Element>();
  const [scrollOptions, setScrollOptions] = useState<OdinDataRetrievalOptions>();
  const [currentRecords, setCurrentRecords] = useState<Array<AssignedRecordItem>>();

  const tagCreator = useTestTag('activity-item');

  const {
    data: myActivityPosts,
    isLoading,
    isFetching,
    isError,
  } = useGetMyActivitiesQuery(
    {
      page: scrollOptions?.page ?? 1,
      pageSize: scrollOptions?.pageSize ?? 50,
    },
    { skip: scrollOptions === undefined }
  );

  const { t } = useTranslation();
  const scrollerSubsciber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const { state } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (isError) {
      //prevent infinite grid scrolling indicator if API returns error
      setCurrentRecords([]);
    } else if (myActivityPosts && !isFetching) {
      setCurrentRecords(myActivityPosts);
    }
  }, [myActivityPosts, isFetching, isError]);

  useEffect(() => {
    if (currentRecords && scrollerSubsciber.current) {
      if (currentRecords.length > 0) {
        scrollerSubsciber.current?.sendData({
          data: currentRecords as unknown as JsonDataItem[],
          requestId: scrollOptions?.requestId,
        });
      } else {
        scrollerSubsciber.current.sendData();
      }
    }
  }, [currentRecords]);

  const actionItemTemplate = useCallback((item: AssignedRecordItem, i: number) => {
    const colorStatusStyles = item.backgroundColor
      ? { backgroundColor: `${item.backgroundColor}`, color: getTextColor(item.backgroundColor) }
      : undefined;

    return (
      <div
        key={i}
        className="mb-2 flex cursor-pointer rounded bg-gray-5"
        onClick={() => {
          navigate(`/${state.user?.currentSchema}/my-activities`, {
            state: {
              item: item,
            },
          });
        }}
        ref={tagCreator()}
      >
        <div
          className="flex h-auto w-1 rounded-l rounded-s-lg border border-r-0 border-gray-4"
          style={colorStatusStyles}
        ></div>
        <div className="flex w-full flex-col rounded-br rounded-tr border border-l-0 border-gray-4 px-2 py-4">
          <h3 className="mb-2">
            <strong>{`${item.status} - ${item.moduleName} #${item.docNo}`}</strong>
          </h3>
          {item.description}
        </div>
      </div>
    );
  }, []);

  const renderItems = (finalData: Array<AssignedRecordItem>) => finalData.map(actionItemTemplate);

  const renderScrollerItems = useCallback((data: JsonData): ReactNode | ReactNode[] => {
    return <div>{renderItems(data as unknown as Array<AssignedRecordItem>)}</div>;
  }, []);

  const dataLoadIndicator = useMemo(() => <ContinuousScrollerDataLoadingIndicator />, []);

  const renderContinuousScroller = useCallback(
    () =>
      scrollElement ? (
        <ContinuousScroller
          dataRetriever={dataRetrieval}
          scrollingElement={scrollElement}
          dataLoadIndicator={dataLoadIndicator}
          className="grow pb-5"
        >
          {renderScrollerItems}
        </ContinuousScroller>
      ) : (
        <></>
      ),
    [scrollElement]
  );

  const onScrollElementCreated = (element: HTMLDivElement) => {
    setScrollElement(element);
  };

  const dataRetrieval = useMemo<OdinDataRetrieval>(() => {
    return {
      getSubscriber: (subscriber) => {
        scrollerSubsciber.current = subscriber;
      },
      getData: async (_subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        setScrollOptions(options);
      },
    };
  }, []);

  const noActivities = useMemo(() => {
    if (scrollOptions && !isLoading && myActivityPosts) {
      return scrollOptions.page === 1 && myActivityPosts?.length === 0;
    }
    return false;
  }, [isLoading, scrollOptions]);

  return (
    <div
      className="flex flex-col overflow-hidden max-sm:w-full sm:w-1/2"
      ref={(element) => {
        if (element) onScrollElementCreated(element);
      }}
    >
      {showHeader && <h2 className="dark:text-white pb-2 text-3xl font-bold">{t('my-activities')}</h2>}
      {isLoading && (
        <div className="flex grow items-center justify-center">
          <Loader title={t('loading-activities')} />
        </div>
      )}

      <div className="custom-scroll flex grow gap-4 overflow-y-scroll pb-3" hidden={isLoading}>
        {noActivities ? (
          <div className="flex h-full grow items-center justify-center text-lg font-bold">{t('nothing-assigned')}</div>
        ) : (
          renderContinuousScroller()
        )}
      </div>
    </div>
  );
};

export default CustomActionScroll;
